import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import supabase from '../supabase/supabase';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { 
  FiMail, 
  FiLock, 
  FiArrowRight, 
  FiAlertCircle, 
  FiLoader, 
  FiChrome // Using Chrome as a representative icon for Google in the Fi set
} = FiIcons;

const Login = () => {
  const [isSignUp, setIsSignUp] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || "/dashboard";

  // Existing Email/Password Logic
  const handleAuth = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      if (isSignUp) {
        const { error: signUpError } = await supabase.auth.signUp({
          email,
          password,
        });
        if (signUpError) throw signUpError;
        alert('Check your email for the confirmation link!');
      } else {
        const { error: signInError } = await supabase.auth.signInWithPassword({
          email,
          password,
        });
        if (signInError) throw signInError;
        navigate(from, { replace: true });
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // New Google Login Logic
  const handleGoogleLogin = async () => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/dashboard`
      }
    });
    if (error) {
      setError(error.message);
      console.error("Google Login Error:", error.message);
    }
  };

  return (
    <div className="min-h-[90vh] flex items-center justify-center px-6 py-12 bg-[#f4f4f4]">
      <div className="w-full max-w-md bg-white border-[4px] border-black brutal-shadow-lg p-8 md:p-10">
        
        {/* Header */}
        <div className="text-center mb-10">
          <h1 className="text-4xl md:text-5xl font-black uppercase tracking-tighter mb-3 leading-none">
            {isSignUp ? 'New Account' : 'Welcome'}
          </h1>
          <p className="font-bold text-gray-500 uppercase text-xs tracking-[0.2em]">
            {isSignUp ? 'Start creating your eternal links' : 'Manage your permanent QR codes'}
          </p>
        </div>

        {error && (
          <div className="bg-red-100 border-[3px] border-black p-4 mb-8 flex items-start gap-3">
            <SafeIcon icon={FiAlertCircle} className="w-5 h-5 mt-0.5 shrink-0" />
            <p className="text-sm font-black uppercase">{error}</p>
          </div>
        )}

        {/* Primary Action: Google Auth */}
        <button 
          onClick={handleGoogleLogin}
          className="w-full bg-white text-black border-[4px] border-black py-4 font-black text-lg uppercase tracking-tight brutal-shadow-sm hover:bg-black hover:text-white transition-all flex items-center justify-center gap-3 mb-8 group"
        >
          <SafeIcon icon={FiChrome} className="w-6 h-6 group-hover:rotate-12 transition-transform" />
          Sign in with Google
        </button>

        {/* Brutalist Divider */}
        <div className="relative flex items-center mb-8">
          <div className="flex-grow border-t-[3px] border-black"></div>
          <span className="flex-shrink mx-4 font-black text-sm uppercase bg-black text-white px-3 py-1 -rotate-2">
            OR
          </span>
          <div className="flex-grow border-t-[3px] border-black"></div>
        </div>

        {/* Secondary Action: Email/Password Form */}
        <form onSubmit={handleAuth} className="space-y-6">
          <div className="space-y-2">
            <label className="text-xs font-black uppercase tracking-widest text-gray-500">Email Address</label>
            <div className="relative">
              <SafeIcon icon={FiMail} className="absolute left-4 top-1/2 -translate-y-1/2 text-black w-5 h-5" />
              <input 
                type="email" 
                required 
                value={email} 
                onChange={(e) => setEmail(e.target.value)}
                className="w-full border-[3px] border-black p-4 pl-12 font-bold focus:outline-none focus:bg-[#f0f0f0] transition-colors"
                placeholder="YOU@EXAMPLE.COM"
              />
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-xs font-black uppercase tracking-widest text-gray-500">Password</label>
            <div className="relative">
              <SafeIcon icon={FiLock} className="absolute left-4 top-1/2 -translate-y-1/2 text-black w-5 h-5" />
              <input 
                type="password" 
                required 
                value={password} 
                onChange={(e) => setPassword(e.target.value)}
                className="w-full border-[3px] border-black p-4 pl-12 font-bold focus:outline-none focus:bg-[#f0f0f0] transition-colors"
                placeholder="••••••••"
              />
            </div>
          </div>

          <button 
            type="submit" 
            disabled={loading}
            className="w-full bg-black text-white border-[3px] border-black py-4 font-black text-lg uppercase brutal-shadow-sm hover:translate-x-1 hover:translate-y-1 hover:shadow-none transition-all flex items-center justify-center gap-2 disabled:opacity-50"
          >
            {loading ? <SafeIcon icon={FiLoader} className="animate-spin" /> : isSignUp ? 'Create Account' : 'Sign In'}
            {!loading && <SafeIcon icon={FiArrowRight} className="w-5 h-5" />}
          </button>
        </form>

        {/* Toggle Sign In / Sign Up */}
        <div className="mt-10 pt-6 border-t-[3px] border-black text-center">
          <button 
            onClick={() => setIsSignUp(!isSignUp)}
            className="font-black text-sm uppercase tracking-tighter hover:bg-black hover:text-white px-4 py-2 border-2 border-transparent hover:border-black transition-all"
          >
            {isSignUp ? 'Already a member? Sign In' : "New here? Create Account"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Login;